// Active-organization state. Roles are org-scoped and user-role assignment is
// per-org, so the whole console operates "within an org" chosen once in the
// layout. This provider loads the org list and tracks the active selection.

import { type ReactNode, createContext, useContext, useMemo, useState } from "react"
import { adminApi } from "../api/endpoints.js"
import type { OrganizationDto } from "../api/types.js"
import { useAdminContext } from "../auth/AdminAuthContext.js"
import { useAsync } from "../lib/useAsync.js"

interface OrgState {
  orgs: OrganizationDto[]
  activeOrg: string | null
  setActiveOrg: (id: string) => void
  loading: boolean
  error: string | null
}

const OrgContext = createContext<OrgState | null>(null)

export function OrgProvider({ children }: { children: ReactNode }) {
  const authContext = useAdminContext()
  const { data, loading, error } = useAsync(() => adminApi.listOrganizations(), [])
  const [override, setOverride] = useState<string | null>(null)

  const orgs = useMemo(() => data ?? [], [data])

  // Default: the org the session authenticated into (if it exists), else the first.
  const activeOrg = useMemo(() => {
    if (override && orgs.some((o) => o.id === override)) return override
    if (authContext && orgs.some((o) => o.id === authContext.org)) return authContext.org
    return orgs[0]?.id ?? null
  }, [override, orgs, authContext])

  const value = useMemo<OrgState>(
    () => ({ orgs, activeOrg, setActiveOrg: setOverride, loading, error }),
    [orgs, activeOrg, loading, error]
  )

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>
}

export function useOrg(): OrgState {
  const ctx = useContext(OrgContext)
  if (!ctx) throw new Error("useOrg must be used within an OrgProvider")
  return ctx
}
